import React, { useEffect, useMemo, useState } from "react";
import { Search, FileText, Download, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {ReactComponent as Devices} from "../../assets/images/Devices.svg";

import PageHeader from "../../components/layout/PageHeader";
import DataTable from "../../components/table/Datatable";
import PaginationFooter from "../../components/table/PaginationFooter";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

// ---------------- PDF Modal ----------------
function PdfModal({ open, title = "บิล", url, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[200] bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="fixed inset-0 z-[201] flex items-center justify-center p-4"
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
          >
            <div className="w-full max-w-6xl rounded-md bg-white shadow-xl overflow-hidden">
              {/* header */}
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={18} className="text-slate-700" />
                  <div className="font-semibold text-slate-900 truncate">{title}</div>
                </div>

                <div className="flex items-center gap-2">
                  {/* download */}
                  <a
                    href={url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-10 items-center gap-2 rounded-md border border-[#00000026] bg-white px-4 text-sm font-semibold text-slate-700 hover:shadow-sm active:scale-[0.98]"
                    title="ดาวน์โหลด"
                  >
                    <Download size={16} />
                    ดาวน์โหลด
                  </a>

                  {/* close */}
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#00000026] bg-white text-slate-700 hover:shadow-sm active:scale-[0.98]"
                    title="ปิด"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* body */}
              <div className="h-[80vh] bg-slate-50">
                {url ? (
                  <iframe
                    title="pdf-preview"
                    src={url}
                    className="h-full w-full"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-slate-500">
                    ไม่พบไฟล์ PDF
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ---------------- mock data ----------------
const mockRows = [
  {
    billNo: "5466",
    orderItem: "iPhone 13 Pro Max",
    accessory: "ป้องกันเลนส์กล้อง Focus iPhone 13 Pro Max",
    imei: "GX55HQXTXG",
    pdfUrl: "/mock/bill-5670.pdf", // ✅ ใส่ไฟล์จริงของคุณได้เลย
  },
  {
    billNo: "",
    orderItem: "iPhone 13 Pro Max",
    accessory: "เคส iPhone 13 Pro Max",
    imei: "",
    pdfUrl: "/mock/bill-5670.pdf",
  },
  {
    billNo: "",
    orderItem: "iPhone 13 Pro Max",
    accessory: "เคส iPhone 13 Pro Max",
    imei: "",
    pdfUrl: "/mock/bill-5670.pdf",
  },
  {
    billNo: "5465",
    orderItem: "iPhone 13",
    accessory: "ป้องกันเลนส์กล้อง Focus iPhone",
    imei: "GX55HQXTXG",
    pdfUrl: "/mock/bill-5670.pdf",
  },
  {
    billNo: "",
    orderItem: "iPhone 13",
    accessory: "เคส iPhone 13",
    imei: "",
    pdfUrl: "/mock/bill-5670.pdf",
  },
];

export default function Accessory() {
  // filters
  const [query, setQuery] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // pdf modal
  const [openPdf, setOpenPdf] = useState(false);
  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  const filtered = useMemo(() => {
    let rows = [...mockRows];

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter((r) =>
        [r.billNo, r.orderItem, r.accessory, r.imei]
          .map((x) => String(x || "").toLowerCase())
          .some((x) => x.includes(q))
      );
    }
    return rows;
  }, [query]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const columns = useMemo(
    () => [
      { key: "billNo", title: "หมายเลขบิล", className: "text-slate-700" },
      { key: "orderItem", title: "รายการออเดอร์", className: "font-semibold" },
      { key: "accessory", title: "อุปกรณ์เสริม" },
      { key: "imei", title: "IMEI", className: "font-semibold" },
      {
        key: "bill",
        title: "บิล",
        render: (r) => (
          <button
            type="button"
            onClick={() => {
              setPdfTitle(`บิล ${r.billNo || ""}`.trim());
              setPdfUrl(r.pdfUrl);
              setOpenPdf(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2 text-sm font-semibold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5]"
          >
            <FileText size={16} />
            บิล
          </button>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-4 p-4 bg-white min-h-full">
      {/* header */}
      <PageHeader
        icon={Devices}
        breadcrumb="รายการสินค้า"
        title="ขายอุปกรณ์เสริม"
        subtitle=""
        showExport={false}
      />

      {/* search bar (ตาม UI: แถบเทา + input ยาว) */}
      <div className="rounded-2xl bg-[#F5F5F5] p-4">
        <div className="flex items-center gap-3 rounded-xl border hover:border-2 border-[#1D161633] bg-white px-4 py-3 w-2/5">
          <Search size={18} className="text-[#1D1616B2]" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            className="w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680]"
            placeholder="ค้นหารายการ..."
          />
        </div>
      </div>

      {/* table */}
      <DataTable
        columns={columns}
        data={paged}
        rowHeight="h-16"
        headerBg="bg-slate-50"
        striped={false}
        tableMinWidth="min-w-[1100px]"
      />

      {/* footer */}
      <PaginationFooter
        totalItems={totalItems}
        page={safePage}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        pageSizeOptions={[10, 20, 50]}
      />

      {/* pdf modal */}
      <PdfModal
        open={openPdf}
        title={pdfTitle || "บิล"}
        url={pdfUrl}
        onClose={() => setOpenPdf(false)}
      />
    </div>
  );
}
